import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { EAbaPreMedico, EAbaConsultorioPrivado, EAbaPrePrestador } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { ServicoGeralResponse } from '@/core/api/sicom-api/servicos/shared/servico-geral.model';
import { PermissionService } from '@/core/permissions/permission.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { efetivacaoSeraExOfficio } from '@/features/servicos/shared/utils/ex-officio.presenter';
import { CatalogoServicosStore } from '@/features/servicos/shared/catalogo-servicos.store';
import { TipoServicoPessoa, obterServicoDisponivelPorCodigo } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { CardServicos, type ConfirmarSolicitacaoPayload, type InserirHistoricoPayload, type ProtocoloResumo, type TramitacaoHistoricoCard, type VisibilidadeAbasConsultorio } from '@/features/servicos/shared/card-servicos/card-servicos';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { EncerramentoLote } from '@/features/servicos/shared/encerramento-lote/encerramento-lote';
import { EncerramentoLoteService } from '@/features/servicos/shared/encerramento-lote/encerramento-lote.service';
import { BiometriaService } from '@/core/api/sicom-api/registro/medico/biometria/biometria.service';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { QuadroProtocolos } from '@/features/servicos/shared/quadro-protocolos/quadro-protocolos';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import {
    formatarDataTramitacao as formatarDataTramitacaoUtil,
    identificadorProtocolo as identificadorProtocoloUtil,
    modalidadeInscricaoRotulo as modalidadeInscricaoRotuloUtil,
    situacaoClasse as situacaoClasseUtil,
    situacaoRotulo as situacaoRotuloUtil
} from '@/features/servicos/shared/utils/protocolo-rotulos.utils';
import { CardContent } from '@/shared/components/card-content/card-content';
import { LoggerService } from '@/core/services/logger.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ImageModule } from 'primeng/image';
import { TextareaModule } from 'primeng/textarea';
import { MessageService } from 'primeng/api';
import { combineLatest } from 'rxjs';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type AbaDashboardServicos = EAbaPrePrestador | EAbaPreMedico | EAbaConsultorioPrivado;

interface ModuloPrePrestador {
    label: string;
    value: AbaDashboardServicos;
    icon: string;
}

interface CancelarSolicitacaoRequest {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufCrm: string;
    codigoServico: string;
}

/** Dashboard de Serviços (PF/PJ): lista os protocolos do serviço selecionado, exibe o detalhe e orquestra confirmar/cancelar/tramitar via facade. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-dashboard-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, FormsModule, QuadroProtocolos, CardServicos, ButtonModule, DialogModule, ImageModule, TextareaModule, CardContent, EncerramentoLote, SelectEstado],
    /**
     * A fila do encerramento em lote entra no MESMO nível do facade de propósito: é o nível que
     * sobrevive à destruição do quadro (`carregandoProtocolos` troca o `KanbanProtocolos` por um
     * spinner e o recria). Uma fila provida lá dentro morreria no primeiro item, levando junto a
     * memória do que o portal já aceitou.
     */
    providers: [DashboardServicosFacade, EncerramentoLoteService],
    templateUrl: './dashboard-servicos.html',
    styleUrls: ['./dashboard-servicos.scss']
})
export class DashboardServicos implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly dashboardServicosFacade = inject(DashboardServicosFacade);
    private readonly ufEfetivaStore = inject(UfEfetivaStore);
    private readonly messageService = inject(MessageService);
    private readonly biometriaService = inject(BiometriaService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggerService);
    private readonly catalogo = inject(CatalogoServicosStore);

    /**
     * O quadro montado agora. `undefined` enquanto o painel de detalhe de um protocolo está aberto —
     * ali o quadro não existe, e não há o que recarregar.
     */
    private readonly quadro = viewChild(QuadroProtocolos);

    readonly tipo = signal<TipoServicoPessoa>('pessoa-fisica');
    readonly selectedComponent = signal('');
    readonly protocoloSelecionado = signal<ProtocoloResumo | null>(null);
    /**
     * Pedido de aviso de pendência em trânsito para o card de detalhe. Two-way: o card o desliga
     * assim que abre o compositor, para que reabrir o protocolo depois não dispare o e-mail de novo.
     */
    readonly avisoPendenciaSolicitado = signal(false);

    /** UF do quadro, lida do store. `ufEfetivaStore` e privado, entao o template precisa deste espelho. */
    readonly ufCrm = computed(() => this.ufEfetivaStore.ufEfetiva() ?? '');

    /** Só o perfil nacional escolhe conselho; para quem tem UF própria não há o que escolher. */
    readonly mostrarSeletorDeUf = computed(() => this.ufEfetivaStore.somenteLeitura());

    /** A UF escolhida, ou `null` enquanto o nacional não escolheu — o seletor mostra o placeholder. */
    readonly ufSelecionada = computed(() => this.ufEfetivaStore.ufEfetiva());

    /**
     * A escolha vira `?uf` na URL, e não estado local: é de lá que o módulo inteiro lê a UF efetiva, e
     * é o que faz a escolha sobreviver a F5 e a link compartilhado. `merge` preserva os outros params.
     */
    onUfSelecionada(uf: string | null): void {
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { uf: uf || null },
            queryParamsHandling: 'merge'
        });
    }

    /** `selectedComponent()` guarda o codigo da rota; o kanban indexa por id do EServico. */
    readonly idServico = computed(() => obterServicoDisponivelPorCodigo(this.selectedComponent())?.id);

    /**
     * Titulo do cabecalho, derivado do catalogo em vez de escrito uma unica vez no subscribe dos
     * params da rota. O catalogo chega por HTTP (`ServicosContent.ngOnInit` dispara `carregarPara`);
     * numa carga fria o subscribe do `combineLatest` abaixo emite sincronamente ANTES da resposta
     * chegar, e um `.set()` ali ficaria preso em 'Servico Selecionado' ate o analista trocar de
     * servico. Sendo computed, o titulo se corrige sozinho assim que `catalogo.rotuloDoCodigo`
     * passar a responder.
     */
    readonly serviceTitle = computed(() => {
        const codigo = this.selectedComponent();
        if (!codigo) {
            return 'Selecione um servico';
        }
        return this.catalogo.rotuloDoCodigo(codigo) ?? 'Servico Selecionado';
    });

    readonly exibirDialogoMotivoCancelamento = signal(false);
    readonly motivoCancelamento = signal('');
    private cancelamentoPendente: { request: CancelarSolicitacaoRequest; tipo: TipoServicoPessoa } | null = null;

    readonly unidadeNome = signal('');
    readonly codigoEstado = signal('');
    readonly abaPrePrestadorSelecionada = signal<AbaDashboardServicos>(EAbaPrePrestador.DADOS);
    readonly exibirMenuAbasPrePrestador = signal(true);
    readonly visibilidadeAbasConsultorio = signal<VisibilidadeAbasConsultorio>({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
    readonly modulosPrePrestador: ModuloPrePrestador[] = [
        { label: 'Dados', value: EAbaPrePrestador.DADOS, icon: 'pi pi-user' },
        { label: 'Funcionamento', value: EAbaPrePrestador.FUNC, icon: 'pi pi-cog' },
        { label: 'Documentos', value: EAbaPrePrestador.DOC, icon: 'pi pi-file' },
        { label: 'Corpo Societário', value: EAbaPrePrestador.SOCIETARIO, icon: 'pi pi-users' },
        { label: 'Corpo Clínico', value: EAbaPrePrestador.CLINICO, icon: 'pi pi-user-plus' },
        { label: 'Especialidades', value: EAbaPrePrestador.ESPECIALIDADES, icon: 'pi pi-briefcase' },
        { label: 'Área de Atuação', value: EAbaPrePrestador.AREA_ATUACAO, icon: 'pi pi-map-marker' },
        { label: 'Serviços', value: EAbaPrePrestador.SERVICOS, icon: 'pi pi-cog' },
        { label: 'Atividades', value: EAbaPrePrestador.ATIVIDADES, icon: 'pi pi-bolt' },
        { label: 'Rec.Materiais', value: EAbaPrePrestador.REC_MATERIAIS, icon: 'pi pi-box' },
        { label: 'Rec.Humanos', value: EAbaPrePrestador.REC_HUMANOS, icon: 'pi pi-users' },
        { label: 'Documentação Pendente', value: EAbaPrePrestador.PENDENTE, icon: 'pi pi-exclamation-circle' },
        { label: 'Descontos', value: EAbaPrePrestador.DESCONTOS, icon: 'pi pi-percentage' },
        { label: 'Anexos', value: EAbaPrePrestador.ANEXOS, icon: 'pi pi-paperclip' }
    ];
    readonly modulosPreMedico: ModuloPrePrestador[] = [
        { label: 'Dados Pessoais', value: EAbaPreMedico.DADOS, icon: 'pi pi-user' },
        { label: 'Documentos', value: EAbaPreMedico.DOC, icon: 'pi pi-id-card' },
        { label: 'Correspondencia', value: EAbaPreMedico.CORR, icon: 'pi pi-envelope' },
        { label: 'Telefone', value: EAbaPreMedico.TEL, icon: 'pi pi-phone' },
        { label: 'Formacao', value: EAbaPreMedico.FORM, icon: 'pi pi-graduation-cap' },
        { label: 'Deficiencias', value: EAbaPreMedico.DEF, icon: 'pi pi-heart' },
        { label: 'Anexos', value: EAbaPreMedico.ANEXOS, icon: 'pi pi-paperclip' }
    ];
    readonly modulosConsultorioPrivado: ModuloPrePrestador[] = [
        { label: 'Dados Gerais', value: EAbaConsultorioPrivado.DADOS, icon: 'pi pi-info-circle' },
        { label: 'Endereco', value: EAbaConsultorioPrivado.ENDERECO, icon: 'pi pi-map-marker' },
        { label: 'Compartilhamento', value: EAbaConsultorioPrivado.COMP, icon: 'pi pi-share-alt' },
        { label: 'Especialidades', value: EAbaConsultorioPrivado.ESP, icon: 'pi pi-th-large' },
        { label: 'Horario Funcionamento', value: EAbaConsultorioPrivado.HORA, icon: 'pi pi-clock' }
    ];

    /**
     * Recarrega o quadro depois que o diálogo do encerramento em lote fecha — UMA vez, e só aí.
     *
     * Recarregar por item seria proibitivo: cada recarga são ~10 chamadas, e a busca de concluídos
     * consulta o portal uma vez por concluído — lista que a própria fila faz crescer, o que torna o
     * custo quadrático no tamanho do lote. E a recarga destrói o `KanbanProtocolos`: uma fila que
     * morasse lá dentro morreria no primeiro item. É por isso que o pedido chega pelo `fechar()` do
     * diálogo, e não pelo fim da fila.
     */
    recarregarQuadroAposLote(): void {
        this.quadro()?.recarregar();
    }

    get servicosGerais(): ServicoGeralResponse[] {
        return this.dashboardServicosFacade.servicosGerais();
    }

    get itensProtocolos(): ItemStatus[] {
        return this.dashboardServicosFacade.itensProtocolos();
    }

    get carregandoProtocolos(): boolean {
        return this.dashboardServicosFacade.carregandoProtocolos();
    }

    get historicoTramitacoes(): TramitacaoHistoricoCard[] {
        return this.dashboardServicosFacade.historicoTramitacoes();
    }

    get carregandoHistorico(): boolean {
        return this.dashboardServicosFacade.carregandoHistorico();
    }

    get erroHistorico(): string | null {
        return this.dashboardServicosFacade.erroHistorico();
    }

    get inserindoHistorico(): boolean {
        return this.dashboardServicosFacade.inserindoHistorico();
    }

    get erroInsercaoHistorico(): string | null {
        return this.dashboardServicosFacade.erroInsercaoHistorico();
    }

    get confirmandoSolicitacao(): boolean {
        return this.dashboardServicosFacade.confirmandoSolicitacao();
    }

    get sucessoConfirmacaoSolicitacao(): string | null {
        return this.dashboardServicosFacade.sucessoConfirmacaoSolicitacao();
    }

    /**
     * Contraparte de `sucessoConfirmacaoSolicitacao`: sem repassá-la ao card, a recusa do servidor
     * ficaria só no facade e a tela não mudaria nada depois de um clique que falhou.
     */
    get erroConfirmacaoSolicitacao(): string | null {
        return this.dashboardServicosFacade.erroConfirmacaoSolicitacao();
    }

    get crmGeradoConfirmacao(): number | null {
        return this.dashboardServicosFacade.crmGeradoConfirmacao();
    }

    ngOnInit(): void {
        const ufEfetivaInicial = this.ufEfetivaStore.ufEfetiva();
        // Mesma guarda de `:258` (nacional sem UF escolhida): sem ela, esta chamada saía com
        // `uf_crm=` vazio em TODO carregamento do dashboard — regressão deste branch, já que o
        // `obterUfCrm()` que ela substituiu nunca devolvia `''`.
        if (ufEfetivaInicial) {
            this.dashboardServicosFacade.carregarServicosGerais(ufEfetivaInicial);
        }

        const parentParams$ = this.route.parent?.paramMap!;
        const childParams$ = this.route.paramMap;
        // A troca de UF do nacional navega mudando só o QUERY param (`?uf`), e o `paramMap` do Angular
        // não reemite quando so ele muda. Sem este observable no combineLatest, o quadro ficava com os
        // cards da UF anterior sob o rotulo da UF nova: `ufCrm()` (computed) ja mostrava a UF certa, mas
        // `carregarProtocolosPorServico` nunca era chamado de novo para busca-la.
        const queryParams$ = this.route.queryParamMap;

        combineLatest([parentParams$, childParams$, queryParams$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([parent, child]) => {
                const tipoParam = parent.get('tipo');
                const tipo: TipoServicoPessoa = tipoParam === 'pessoa-juridica' ? 'pessoa-juridica' : 'pessoa-fisica';
                const codigo = child.get('codigo') ?? '';

                this.tipo.set(tipo);
                this.selectedComponent.set(codigo);
                this.protocoloSelecionado.set(null);
                this.unidadeNome.set('');
                this.codigoEstado.set('');
                this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
                this.exibirMenuAbasPrePrestador.set(true);
                this.visibilidadeAbasConsultorio.set({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
                this.dashboardServicosFacade.limparHistoricoTramitacao();

                if (!codigo) {
                    this.dashboardServicosFacade.limparProtocolos();
                    return;
                }

                const ufCrm = this.ufEfetivaStore.ufEfetiva() ?? '';
                // Nacional que ainda nao escolheu conselho nao tem UF: consultar com string vazia devolveria
                // nada e a tela leria como "nao ha protocolos", que e a mentira que precisamos evitar.
                if (!ufCrm) {
                    return;
                }

                this.dashboardServicosFacade.carregarProtocolosPorServico(codigo, [ufCrm]);
            });

        this.dashboardServicosFacade.alteracaoSolicitacaoConcluida$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((evento) => {
            // Detalhe segurado quando há CRM para o analista ler: fechar aqui destruiria o diálogo
            // (o card vive sob um @if estrutural) e o número nunca seria visto.
            if (evento.manterDetalheAberto) {
                // A tramitação de fato mudou — a própria efetivação acabou de acrescentar uma linha
                // nela, no portal — e `limparHistoricoTramitacao` (chamada dentro da efetivação) já
                // zerou a lista em tela sem buscar de novo. Sem recarregar aqui, quem estivesse na
                // aba Histórico leria "Nenhuma tramitação encontrada", como se ela nunca tivesse
                // existido.
                this.recarregarHistoricoTramitacao();
            } else {
                this.protocoloSelecionado.set(null);
                // Só reseta a aba quando o detalhe de fato FECHA: com ele aberto (CRM gerado), o
                // analista pode estar em outra aba (ex.: Corpo Clínico do pré-prestador) e não pediu
                // para voltar a Dados — antes o detalhe inteiro sumia e o reset era invisível; hoje
                // ele ficaria visível e se leria como um salto sem motivo.
                this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
            }
            this.exibirMenuAbasPrePrestador.set(true);
            this.visibilidadeAbasConsultorio.set({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
        });
    }

    onItemSelected(item: ItemStatus): void {
        this.aplicarProtocoloSelecionado(item);
    }

    /**
     * O quadro moveu o card para a coluna de pendências com "Avisar ao médico?" marcado.
     *
     * Abre o protocolo e pede o aviso ao card de detalhe, nesta ordem: quem monta o e-mail (template,
     * destinatários, dados do serviço) é o card, e ele trabalha em cima do protocolo selecionado.
     * Pedir o aviso antes de selecionar montaria o e-mail do protocolo anterior — ou de nenhum.
     */
    onAvisoPendenciaSolicitado(item: ItemStatus): void {
        this.aplicarProtocoloSelecionado(item);
        this.avisoPendenciaSolicitado.set(true);
    }

    private aplicarProtocoloSelecionado(item: ItemStatus): void {
        this.unidadeNome.set(item.empresa);
        this.codigoEstado.set(item.crmUf ?? '');
        this.protocoloSelecionado.set({
            portal: item.portal,
            sas: item.sas,
            avatar: item.avatar,
            numeroProtocolo: item.numeroProtocolo,
            anoProtocolo: item.anoProtocolo,
            ufCrm: this.ufEfetivaStore.ufEfetiva() ?? '',
            cnpj: item.cnpj,
            diretorTecnicoNome: item.diretorTecnicoNome,
            diretorTecnicoCrmUf: item.diretorTecnicoCrmUf,
            situacaoCodigo: item.situacaoCodigo,
            // Vai junto porque é dela que o card conta os 30 dias do ex officio. Sem esta linha o
            // detalhe recebe um protocolo sem abertura, `diasDesde` devolve `null` e a inscrição de
            // ofício nunca fica disponível pelo botão — enquanto o quadro, que lê o `ItemStatus`
            // inteiro, oferece o mesmo ato no mesmo protocolo. É o desacordo entre os dois gestos.
            dataSolicitacao: item.dataSolicitacao,
            inPago: item.inPago,
            isAnuidadePaga: item.isAnuidadePaga,
            modalidadeInscricao: item.modalidadeInscricao,
            ultimaTramitacao: item.ultimaTramitacao
                ? {
                      numeroProtocolo: item.ultimaTramitacao.numeroProtocolo,
                      anoProtocolo: item.ultimaTramitacao.anoProtocolo,
                      ufProtocolo: item.ultimaTramitacao.ufProtocolo ?? item.ultimaTramitacao.uf_protocolo,
                      textoInformativo: item.ultimaTramitacao.textoInformativo,
                      dataTramitacao: item.ultimaTramitacao.dataTramitacao
                  }
                : undefined,
            status: item.status as ProtocoloResumo['status']
        });
        this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
        this.exibirMenuAbasPrePrestador.set(true);
        this.visibilidadeAbasConsultorio.set({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
        this.dashboardServicosFacade.limparHistoricoTramitacao();

        let crm = '';
        let uf = '';

        if (this.tipo() === 'pessoa-fisica' && item.crmUf) {
            const partes = item.crmUf.split('-');
            if (partes.length === 2) {
                crm = partes[0].trim();
                uf = partes[1].trim();
            } else {
                crm = item.crmUf.trim();
                uf = item.ufCrm || '';
            }
        } else if (this.tipo() === 'pessoa-juridica' && item.diretorTecnicoCrmUf) {
            const partes = item.diretorTecnicoCrmUf.split('-');
            if (partes.length === 2) {
                crm = partes[0].trim();
                uf = partes[1].trim();
            } else {
                crm = item.diretorTecnicoCrmUf.trim();
                uf = item.ufCrm || '';
            }
        }

        const crmNumero = parseInt(crm, 10);
        if (!isNaN(crmNumero) && uf) {
            this.biometriaService
                .buscarFotoPorCrmUf(crmNumero, uf)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (res) => {
                        if (res && res.imagemBase64 && this.protocoloSelecionado()) {
                            const base64 = res.imagemBase64;
                            const avatar = base64.startsWith('data:') ? base64 : 'data:image/jpeg;base64,' + base64;
                            this.protocoloSelecionado.update((protocolo) => (protocolo ? { ...protocolo, avatar } : protocolo));
                        }
                    },
                    error: (err) => {
                        this.logger.error('Erro ao carregar foto do medico/diretor:', err);
                    }
                });
        }
    }

    voltarParaLista(): void {
        this.protocoloSelecionado.set(null);
        this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
        this.exibirMenuAbasPrePrestador.set(true);
        this.visibilidadeAbasConsultorio.set({ mostrarCompartilhamento: true, mostrarEspecialidades: true });
        this.dashboardServicosFacade.limparHistoricoTramitacao();
    }

    selecionarAbaPrePrestador(aba: AbaDashboardServicos): void {
        this.abaPrePrestadorSelecionada.set(aba);
    }

    onHistoricoSelecionado(): void {
        this.exibirMenuAbasPrePrestador.set(false);

        const protocolo = this.protocoloSelecionado();
        if (!protocolo?.numeroProtocolo || !protocolo?.anoProtocolo) {
            this.dashboardServicosFacade.definirErroHistorico('Protocolo sem numero/ano para consultar tramitacoes.');
            return;
        }

        const ufProtocolo = (protocolo.ufCrm ?? this.ufEfetivaStore.ufEfetiva() ?? '').trim().toUpperCase();
        if (!ufProtocolo) {
            this.dashboardServicosFacade.definirErroHistorico('UF do protocolo nao informada para consulta de tramitacoes.');
            return;
        }

        this.dashboardServicosFacade.carregarHistoricoTramitacao({
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            uf: ufProtocolo,
            protocoloSelecionado: protocolo
        });
    }

    /**
     * Busca de novo a tramitação do protocolo aberto, em silêncio (sem tocar em `exibirMenuAbasPrePrestador`
     * nem em qual aba está ativa). Chamada só quando o detalhe fica aberto após uma efetivação: a
     * tramitação de fato mudou no portal, e sem isto quem estivesse na aba Histórico ficaria olhando
     * para a lista que `limparHistoricoTramitacao` zerou, sem o retorno de uma nova busca.
     */
    private recarregarHistoricoTramitacao(): void {
        const protocolo = this.protocoloSelecionado();
        if (!protocolo?.numeroProtocolo || !protocolo?.anoProtocolo) {
            return;
        }

        const ufProtocolo = (protocolo.ufCrm ?? this.ufEfetivaStore.ufEfetiva() ?? '').trim().toUpperCase();
        if (!ufProtocolo) {
            return;
        }

        this.dashboardServicosFacade.carregarHistoricoTramitacao({
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            uf: ufProtocolo,
            protocoloSelecionado: protocolo
        });
    }

    onVisualizarSelecionado(): void {
        this.exibirMenuAbasPrePrestador.set(true);
    }

    get modulosAbasAtivas(): ModuloPrePrestador[] {
        if (this.selectedComponent() === 'pre-medico') {
            return this.modulosPreMedico;
        }

        if (
            this.selectedComponent() === 'consultorio-privado' ||
            this.selectedComponent() === 'cadastro-consultorio-privado' ||
            this.selectedComponent() === 'consultorio-privado-renovacao' ||
            this.selectedComponent() === 'renovacao-consultorio-privado'
        ) {
            return this.modulosConsultorioPrivado.filter((modulo) => {
                if (modulo.value === EAbaConsultorioPrivado.COMP) {
                    return this.visibilidadeAbasConsultorio().mostrarCompartilhamento;
                }

                if (modulo.value === EAbaConsultorioPrivado.ESP) {
                    return this.visibilidadeAbasConsultorio().mostrarEspecialidades;
                }

                return true;
            });
        }

        return this.modulosPrePrestador;
    }

    onInserirHistoricoSolicitado(payload: InserirHistoricoPayload): void {
        const protocolo = this.protocoloSelecionado();
        if (!protocolo?.numeroProtocolo || !protocolo?.anoProtocolo) {
            this.dashboardServicosFacade.definirErroInsercaoHistorico('Protocolo sem numero/ano para inserir tramitacao.');
            return;
        }

        const ufProtocolo = (protocolo.ufCrm ?? this.ufEfetivaStore.ufEfetiva() ?? '').trim().toUpperCase();
        if (!ufProtocolo) {
            this.dashboardServicosFacade.definirErroInsercaoHistorico('UF do protocolo nao informada para inserir tramitacao.');
            return;
        }

        this.dashboardServicosFacade.inserirHistoricoTramitacao({
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            ufProtocolo,
            payload,
            protocoloSelecionado: protocolo
        });
    }

    // `protocolo` tem o selecionado como padrao para a tela de detalhe; o card do quadro passa o seu
    // proprio, ja que ali nao existe "selecionado" (a acao age direto na lista, sem abrir o detalhe).
    onConfirmarSolicitacao(payload?: ConfirmarSolicitacaoPayload, protocolo = this.protocoloSelecionado()): void {
        this.dashboardServicosFacade.limparFeedbackConfirmacaoSolicitacao();
        const codigoServico = this.selectedComponent();
        const servicosConfirmaveis = new Set<string>([
            'averbacao-nome',
            'averbacao-nacionalidade',
            'cancelamento-falecimento',
            'cancelar-por-falecimento',
            'cancelamento-inscricao',
            'cancelamento-de-inscricao',
            'cancelamento-pj',
            'alteracao-cadastral',
            'alteracao-contratual',
            'visto-alteracao-contratual',
            'consultorio-privado',
            'cadastro-consultorio-privado',
            'consultorio-privado-renovacao',
            'renovacao-consultorio-privado',
            'pcmso',
            'responsavel-pcmso',
            'visto-corrido',
            'pos-graduacao',
            'pre-medico',
            'pre-prestador',
            'regimento-interno',
            'regimento-interno-corpo-clinico',
            'solicitacao-diretor',
            'diretor-tecnico-solicitacao-pj',
            'solicitacao-diretor-tecnico',
            'diretor-tecnico-solicitacao-pf',
            'diretor-clinico',
            'diretoria-clinica',
            'renovacao-cadastral',
            'renovacao',
            'cart-evento',
            'comissao-etica'
        ]);

        if (!servicosConfirmaveis.has(codigoServico)) {
            return;
        }

        if (!protocolo?.numeroProtocolo || !protocolo?.anoProtocolo) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Protocolo sem numero/ano para confirmar solicitacao.'
            });
            return;
        }

        const ufCrm = (protocolo.ufCrm ?? this.ufEfetivaStore.ufEfetiva() ?? '').trim().toUpperCase();
        if (!ufCrm) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'UF do protocolo nao informada para confirmar solicitacao.'
            });
            return;
        }

        const request = {
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            ufCrm,
            codigoServico,
            dtCancelamento: payload?.dtCancelamento,
            dtHomologacao: payload?.dtHomologacao,
            dataDiretoria: payload?.dataDiretoria,
            observacao: payload?.observacao,
            // O ato que a tela NOMEOU viaja com o pedido, decidido pela mesma função que liberou o
            // botão e escreveu a confirmação. Recalculá-lo no facade abriria a hipótese de o analista
            // autorizar a inscrição de ofício e o portal receber a efetivação comum (ou o contrário),
            // com o retrato do protocolo mudando entre o clique e a chamada.
            exOfficio: efetivacaoSeraExOfficio(codigoServico, protocolo, Date.now(), () => this.permissionService.can(PERMISSOES.pessoaJuridica.prePrestador.exOfficio, { uf: ufCrm }))
        };

        if (codigoServico === 'averbacao-nacionalidade') {
            this.dashboardServicosFacade.confirmarSolicitacaoAverbacaoNacionalidade(request);
            return;
        }

        if (codigoServico === 'cancelamento-falecimento' || codigoServico === 'cancelar-por-falecimento') {
            this.dashboardServicosFacade.confirmarSolicitacaoCancelamentoFalecimento(request);
            return;
        }

        if (codigoServico === 'cancelamento-inscricao' || codigoServico === 'cancelamento-de-inscricao') {
            this.dashboardServicosFacade.confirmarSolicitacaoCancelamentoInscricao(request);
            return;
        }

        if (codigoServico === 'cancelamento-pj') {
            this.dashboardServicosFacade.confirmarSolicitacaoCancelamentoPj(request);
            return;
        }

        if (codigoServico === 'alteracao-cadastral' || codigoServico === 'alteracao-contratual' || codigoServico === 'visto-alteracao-contratual') {
            this.dashboardServicosFacade.confirmarSolicitacaoAlteracaoCadastralPj(request);
            return;
        }

        if (codigoServico === 'consultorio-privado' || codigoServico === 'cadastro-consultorio-privado' || codigoServico === 'consultorio-privado-renovacao' || codigoServico === 'renovacao-consultorio-privado') {
            this.dashboardServicosFacade.confirmarSolicitacaoConsultorioPrivado(request);
            return;
        }

        if (codigoServico === 'pcmso' || codigoServico === 'responsavel-pcmso') {
            this.dashboardServicosFacade.confirmarSolicitacaoPcmso(request);
            return;
        }

        if (codigoServico === 'visto-corrido' || codigoServico === 'visto-fracionado') {
            this.dashboardServicosFacade.confirmarSolicitacaoVistoCorrido(request);
            return;
        }
        if (codigoServico === 'pos-graduacao') {
            this.dashboardServicosFacade.confirmarSolicitacaoPosGraduacao(request);
            return;
        }

        if (codigoServico === 'pre-medico') {
            this.dashboardServicosFacade.confirmarSolicitacaoPreMedico(request);
            return;
        }

        if (codigoServico === 'pre-prestador') {
            this.dashboardServicosFacade.confirmarSolicitacaoPrePrestador(request);
            return;
        }

        if (codigoServico === 'regimento-interno' || codigoServico === 'regimento-interno-corpo-clinico') {
            this.dashboardServicosFacade.confirmarSolicitacaoRegimentoInterno(request);
            return;
        }

        if (codigoServico === 'comissao-etica') {
            this.dashboardServicosFacade.confirmarSolicitacaoComissaoEtica(request);
            return;
        }

        if (codigoServico === 'solicitacao-diretor' || codigoServico === 'diretor-tecnico-solicitacao-pj') {
            this.dashboardServicosFacade.confirmarSolicitacaoSolicitacaoDiretor(request);
            return;
        }

        if (codigoServico === 'solicitacao-diretor-tecnico' || codigoServico === 'diretor-tecnico-solicitacao-pf') {
            this.dashboardServicosFacade.confirmarSolicitacaoSolicitacaoDiretorTecnico(request);
            return;
        }

        if (codigoServico === 'diretor-clinico' || codigoServico === 'diretoria-clinica') {
            this.dashboardServicosFacade.confirmarSolicitacaoDiretorClinico(request);
            return;
        }

        if (codigoServico === 'renovacao-cadastral' || codigoServico === 'renovacao') {
            this.dashboardServicosFacade.confirmarSolicitacaoRenovacaoPj(request);
            return;
        }

        if (codigoServico === 'cart-evento') {
            this.dashboardServicosFacade.confirmarSolicitacaoCartEvento(request);
            return;
        }

        this.dashboardServicosFacade.confirmarSolicitacaoAverbacaoNome(request);
    }

    // Age sempre sobre o protocolo SELECIONADO: o unico caminho ate aqui e o card da tela de detalhe,
    // e ali "selecionado" e justamente o protocolo aberto. O parametro que permitia agir sobre outro
    // existia para o card do quadro, que deixou de chamar este metodo quando efetivar e cancelar pelo
    // menu passaram a concluir o card pelo dialogo do proprio quadro.
    onCancelarSolicitacao(): void {
        const protocolo = this.protocoloSelecionado();

        this.dashboardServicosFacade.limparFeedbackConfirmacaoSolicitacao();

        if (!protocolo?.numeroProtocolo || !protocolo?.anoProtocolo) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Protocolo sem numero/ano para cancelar solicitacao.'
            });
            return;
        }

        const ufCrm = (protocolo.ufCrm ?? this.ufEfetivaStore.ufEfetiva() ?? '').trim().toUpperCase();
        if (!ufCrm) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'UF do protocolo nao informada para cancelar solicitacao.'
            });
            return;
        }

        const request: CancelarSolicitacaoRequest = {
            numeroProtocolo: protocolo.numeroProtocolo,
            anoProtocolo: protocolo.anoProtocolo,
            ufCrm,
            codigoServico: this.selectedComponent()
        };

        this.cancelamentoPendente = { request, tipo: this.tipo() };
        this.motivoCancelamento.set('');
        this.exibirDialogoMotivoCancelamento.set(true);
    }

    confirmarCancelamentoComMotivo(): void {
        const pendente = this.cancelamentoPendente;
        const motivo = this.motivoCancelamento().trim();
        if (!pendente || !motivo) {
            return;
        }

        this.exibirDialogoMotivoCancelamento.set(false);

        if (pendente.tipo === 'pessoa-juridica') {
            this.dashboardServicosFacade.cancelarSolicitacaoPessoaJuridica(pendente.request, motivo);
        } else {
            this.dashboardServicosFacade.cancelarSolicitacaoPessoaFisica(pendente.request, motivo);
        }

        this.cancelamentoPendente = null;
    }

    fecharDialogoMotivoCancelamento(): void {
        this.exibirDialogoMotivoCancelamento.set(false);
        this.cancelamentoPendente = null;
        this.motivoCancelamento.set('');
    }

    onVisibilidadeAbasConsultorioAlterada(visibilidade: VisibilidadeAbasConsultorio): void {
        this.visibilidadeAbasConsultorio.set(visibilidade);

        if (this.abaPrePrestadorSelecionada() === EAbaConsultorioPrivado.COMP && !visibilidade.mostrarCompartilhamento) {
            this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
            return;
        }

        if (this.abaPrePrestadorSelecionada() === EAbaConsultorioPrivado.ESP && !visibilidade.mostrarEspecialidades) {
            this.abaPrePrestadorSelecionada.set(EAbaPrePrestador.DADOS);
        }
    }

    statusClasse(status?: ItemStatus['status'] | ProtocoloResumo['status']): string {
        switch (status) {
            case 'ACEITOU':
                return 'accepted';
            case 'RECUSOU':
                return 'rejected';
            case 'AGUARDANDO':
                return 'waiting';
            default:
                return 'no-indication';
        }
    }

    situacaoProtocoloClasse(codigo?: string): string {
        return situacaoClasseUtil(codigo);
    }

    situacaoProtocoloRotulo(codigo?: string): string {
        return situacaoRotuloUtil(codigo);
    }

    modalidadeInscricaoRotulo(modalidade?: string): string {
        return modalidadeInscricaoRotuloUtil(modalidade);
    }

    indicacaoDiretorTecnicoRotulo(status?: ItemStatus['indicacaoDiretorTecnico']): string {
        switch (status) {
            case 'ACEITOU':
                return 'Aceitou';
            case 'RECUSOU':
                return 'Recusou';
            case 'AGUARDANDO':
                return 'Aguardando';
            case 'NAO_SOCIO':
                return 'Nao socio';
            default:
                return '';
        }
    }

    indicacaoDiretorTecnicoClasse(status?: ItemStatus['indicacaoDiretorTecnico']): string {
        switch (status) {
            case 'ACEITOU':
                return 'aceitou';
            case 'RECUSOU':
                return 'recusou';
            case 'AGUARDANDO':
                return 'aguardando';
            case 'NAO_SOCIO':
                return 'nao-socio';
            default:
                return '';
        }
    }

    /** Identificador do card, no mesmo formato do quadro (ver util). */
    identificadorProtocolo(protocolo: ProtocoloResumo): string {
        return identificadorProtocoloUtil(protocolo);
    }

    /**
     * O rosto do card é de OUTRA pessoa que não o titular do protocolo quando o solicitante é pessoa
     * jurídica: o sujeito é a empresa, mas quem está sob análise é o médico diretor técnico. É a
     * mesma condição que `carregarFotoDoMedico` já usa para escolher DE QUEM buscar o CRM na
     * biometria — resolvida num lugar só, para o rótulo não anunciar "diretor técnico" enquanto a
     * foto exibida veio do titular.
     */
    private rostoEhDoDiretorTecnico(): boolean {
        return this.tipo() === 'pessoa-juridica';
    }

    /**
     * Rótulo do avatar quando ele NÃO é decorativo. Só a pessoa jurídica tem: ali o rosto é do
     * diretor técnico e o nome ao lado é o da EMPRESA — sem rótulo, nem o leitor de tela nem quem
     * enxerga sabem de quem é aquele rosto. Vazio na pessoa física, onde a foto retrata exatamente o
     * nome que já está ao lado dela e anunciá-la repetiria o que já se lê.
     */
    rotuloAvatar(protocolo: ProtocoloResumo): string {
        if (!this.rostoEhDoDiretorTecnico()) {
            return '';
        }

        const nome = (protocolo.diretorTecnicoNome ?? '').trim();
        return nome ? `Diretor técnico: ${nome}` : 'Diretor técnico';
    }

    /**
     * Iniciais do avatar sem foto — médico sem foto cadastrada é o caso comum, não a exceção, e sem
     * elas o card fica com um buraco no lugar do rosto. Na pessoa jurídica saem do nome do DIRETOR
     * TÉCNICO: cair nas da empresa seria mostrar as iniciais de quem não é o retratado.
     *
     * Vazio quando não há nome nenhum de quem retratar — melhor não desenhar avatar do que desenhar
     * um genérico que sugere uma pessoa que ninguém sabe quem é.
     */
    iniciaisAvatar(protocolo: ProtocoloResumo): string {
        const nome = (this.rostoEhDoDiretorTecnico() ? protocolo.diretorTecnicoNome : this.unidadeNome())?.trim();
        return nome ? this.iniciaisSolicitante(nome) : '';
    }

    /**
     * Linha sob o nome, que é por onde o analista confere se o protocolo é de quem ele procura:
     * CRM/UF quando há, senão o CNPJ. O CNPJ é o caso da pessoa jurídica — sem ele a linha sumia
     * justo no pré-prestador, onde a empresa ainda não tem CRM, e o card ficava sem identificação
     * nenhuma. Vazio quando não há nem um nem outro: melhor a linha não existir do que existir vazia.
     */
    identificacaoSolicitante(protocolo: ProtocoloResumo): string {
        const crmUf = this.codigoEstado().trim();
        if (crmUf) {
            return crmUf;
        }

        const cnpj = (protocolo.cnpj ?? '').trim();
        return cnpj ? `CNPJ ${cnpj}` : '';
    }

    ultimaTramitacaoResumo(): string {
        const tramitacao = this.protocoloSelecionado()?.ultimaTramitacao;
        if (!tramitacao) {
            return 'Não informada';
        }

        const data = formatarDataTramitacaoUtil(tramitacao.dataTramitacao);

        return data || 'Não informada';
    }

    iniciaisSolicitante(nome?: string): string {
        const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
        if (!partes.length) {
            return 'M';
        }

        return partes
            .slice(0, 2)
            .map((parte) => parte.charAt(0))
            .join('')
            .toUpperCase();
    }

    ngOnDestroy(): void {
        this.dashboardServicosFacade.destruir();
    }
}
